
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { ArrowUp } from "lucide-react";
import { format } from "date-fns";

interface PerformanceChartProps {
  title?: string;
  data: Array<{ 
    date: string; 
    displayDate?: string;
    value: number; 
    address?: string;
    rank?: number;
  }>;
  className?: string;
  tooltipContent?: string;
}

export function PerformanceChart({ title = "Performance", data, className, tooltipContent }: PerformanceChartProps) {
  if (!data.length) return null;

  const formatReward = (value: number) => {
    return value.toFixed(6);
  };

  const formatRank = (value: number) => {
    return `#${value}`;
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">
          <div className="flex flex-col space-y-2">
            <span>{title}</span>
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-4">
                <span className="flex items-center text-purple-500">
                  <ArrowUp className="h-4 w-4 mr-1" />
                  {tooltipContent || "Node Ranking"}
                </span>
              </div>
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <XAxis 
                dataKey="displayDate" 
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                height={30}
                tickMargin={5}
                scale="point"
                padding={{ left: 10, right: 10 }}
              />
              <YAxis
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={formatReward}
                width={80}
              />
              <YAxis
                yAxisId="rank"
                orientation="right"
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={formatRank}
                width={40}
                reversed={true}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--background))",
                  border: "1px solid hsl(var(--border))",
                }}
                labelStyle={{ color: "hsl(var(--foreground))" }}
                formatter={(value: number, name: string) => {
                  if (name === "Node Ranking") {
                    return [`#${value}`, name];
                  }
                  return [formatReward(value), "Rewards"];
                }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#6e46ae"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                name="Rewards"
              />
              <Line
                yAxisId="rank"
                type="monotone"
                dataKey="rank"
                stroke="#9333ea"
                strokeWidth={1}
                dot={false}
                isAnimationActive={false}
                name="Node Ranking"
                strokeDasharray="4 4"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
